"use client";

import LoadingDog from "@/components/ui/loading-components/LoadingDog";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface GenericModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  onApply?: () => void;
  className?: string;
  isLoading?: boolean;
  zIndex?: number;
}

const GenericModal: React.FC<GenericModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  onApply,
  className = "",
  isLoading,
  zIndex = 99999,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Check if className contains width-related classes
  const hasCustomWidth = className.includes("max-w-") || className.includes("w-");
  const defaultWidthClasses = "max-w-[95%] md:max-w-[90%] lg:max-w-[80%]";

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking on AG-Grid elements or any dropdown/overlay elements
      // AG-Grid specific selectors
      const isAgGridElement =
        target.closest(".ag-popup") ||
        target.closest(".ag-overlay") ||
        target.closest(".ag-menu") ||
        target.closest(".ag-select-list") ||
        target.closest(".ag-filter") ||
        target.closest(".ag-column-select") ||
        target.closest(".ag-tool-panel") ||
        target.closest(".ag-floating-filter-button") ||
        target.closest(".ag-paging-panel") ||
        target.closest(".ag-popup-editor") ||
        target.closest(".ag-popup-backdrop") ||
        target.closest(".ag-rich-select-list") ||
        target.closest(".ag-list-item") ||
        target.closest(".ag-virtual-list-viewport");

      // Ant Design popup selectors (DatePicker, Select, etc.)
      const isAntPopup =
        target.closest(".ant-picker-dropdown") ||
        target.closest(".ant-select-dropdown") ||
        target.closest(".ant-popover") ||
        target.closest(".ant-dropdown");

      // Generic dropdown/overlay selectors
      const isDropdownElement =
        target.closest('[role="tooltip"]') ||
        target.closest('[role="listbox"]') ||
        target.closest('[role="presentation"]') ||
        target.closest(".dropdown-content") ||
        target.closest(".select-dropdown");

      // If clicking on any of these elements, don't close the modal
      if (isAgGridElement || isAntPopup || isDropdownElement) {
        return;
      }

      // Check if we're clicking outside the modal
      if (modalRef.current && !modalRef.current.contains(target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-modal-overlay
          className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto"
          style={{ zIndex }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
            ref={modalRef}
            className={`bg-white rounded-xl p-8 relative my-8 ${hasCustomWidth ? "" : defaultWidthClasses} ${className}`}
            style={{ zIndex: zIndex + 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <div className="relative mb-4">
                <div className="text-center pr-8">
                  <h3 className="font-bold text-xl text-gray-900">{title}</h3>
                  {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
                </div>
                <button
                  type="button"
                  className="btn btn-xs btn-circle btn-ghost absolute -right-2 -top-2"
                  onClick={onClose}
                >
                  ✕
                </button>
              </div>
            )}
            {!title && (
              <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2 z-10"
                onClick={onClose}
              >
                ✕
              </button>
            )}

            <div className="flex flex-col max-h-[calc(90vh-8rem)] overflow-y-auto overflow-x-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <LoadingDog size={16} />
                </div>
              ) : (
                children
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (!isMounted || typeof document === "undefined") {
    return null;
  }

  return createPortal(modalContent, document.body);
};

export default GenericModal;
